import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signup } from '../../../api/reg';
import logo from '../../../app_icon.svg';
import FloatingLabelInput from '../../../common/components/FloatingLabelInput';
import PhoneInput from '../../../common/components/PhoneInput';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userType = location.state?.userType || 'individual';

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [bin, setBin] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isAgreed, setIsAgreed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleBackClick = () => {
        navigate('/sign');
    };

    const validateEmail = (emailValue) => {
        if (!emailValue.trim()) return '';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) return t('invalidEmailFormat');
        const allowedDomains = ['icloud.com', 'gmail.com', 'mail.ru'];
        const domain = emailValue.split('@')[1]?.toLowerCase();
        if (!allowedDomains.includes(domain)) return t('invalidEmailDomain');
        return '';
    };

    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);
        setEmailError(validateEmail(emailValue));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setErrorMessage(t('enter_full_name'));
            return;
        }
        if (!phone.trim()) {
            setErrorMessage(t('enter_phone'));
            return;
        }
        if (userType === 'business' && !bin.trim()) {
            setErrorMessage(t('enter_bin'));
            return;
        }
        if (email.trim()) {
            const emailValidationError = validateEmail(email);
            if (emailValidationError) {
                setErrorMessage(emailValidationError);
                return;
            }
        }

        const countryCode = selectedCountry?.code || '+7';
        const sanitizedPhoneNumber = phone.replace(/\D/g, '');
        const formattedPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;

        setIsLoading(true);
        try {
            const data = await signup(name, formattedPhoneNumber, userType, bin, email.trim() || null);
            monitoringService.trackRegistration(formattedPhoneNumber, false, null).catch(err => {
                console.warn('Failed to track registration attempt:', err);
            });
            navigate('/sms-code', {
                state: {
                    taskId: data.links[0].href.split('/').pop(),
                    phoneNumber: formattedPhoneNumber,
                    typeAuth: 'signup',
                },
            });
        } catch (err) {
            const errorMessageText = err.message || 'Ошибка при регистрации пользователя';
            monitoringService.trackRegistration(formattedPhoneNumber, false, errorMessageText).catch(err => {
                console.warn('Failed to track registration failure:', err);
            });
            setErrorMessage(errorMessageText);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Section - Decorative */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('/images/sign.png')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#147F26]/95 via-[#0f6820]/90 to-[#0a4d16]/95" />

                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-64 h-64 border-2 border-white rounded-full" />
                    <div className="absolute bottom-32 right-16 w-48 h-48 border-2 border-white rounded-full" />
                    <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white rounded-full" />
                </div>

                <div className="relative z-10 flex flex-col justify-center px-16 py-12 text-white">
                    <h1 className="text-4xl font-bold mb-4 leading-tight">
                        {t('joinAgroland') || 'Agroland қауымдастығына қосылыңыз'}
                    </h1>
                    <p className="text-lg text-white/80 mb-8">
                        {t('registerSubtitle') || 'Оңай тіркелу және жарнамаларды басқару'}
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('regFeature1') || 'Тегін тіркелу'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('regFeature2') || 'Жарнамаларды тегін қосу'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('regFeature3') || 'Жылдам қолдау'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section - Form */}
            <div className="w-full lg:w-1/2 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 lg:px-12 lg:py-6 flex-shrink-0">
                    <button
                        onClick={handleBackClick}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#147F26] transition-colors"
                    >
                        <div className="w-10 h-10 rounded-full bg-[#147F26]/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{t('back') || 'Артқа'}</span>
                    </button>

                    <img src={logo} alt="Agroland" className="h-10 lg:h-12" />
                </div>

                {/* Form Area */}
                <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-16 lg:py-6">
                    <div className="w-full max-w-md mx-auto">
                        {/* Mobile Logo */}
                        <div className="lg:hidden flex justify-center mb-8">
                            <img src={logo} alt="Agroland" className="h-14" />
                        </div>

                        <div className="flex items-center gap-3 mb-6">
                            {/* User Type Badge */}
                            <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                                userType === 'business'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-green-100 text-green-700'
                            }`}>
                                {userType === 'business'
                                    ? (t('businessAccount') || 'Кәсіпкерлік')
                                    : (t('individualAccount') || 'Жеке')}
                            </div>
                        </div>

                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                            {t('signUp') || 'Тіркелу'}
                        </h2>
                        <p className="text-gray-500 mb-8">
                            {t('registerPageSubtitle') || 'Жаңа аккаунт құрыңыз'}
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {userType === 'business' && (
                                <FloatingLabelInput
                                    label={t('companyId') || 'БИН/ИИК'}
                                    value={bin}
                                    onChange={(e) => setBin(e.target.value)}
                                />
                            )}

                            <FloatingLabelInput
                                label={userType === 'business' ? t('companyName') : t('nameLabel')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />

                            <PhoneInput
                                label={t('enter_phone')}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                onCountryChange={setSelectedCountry}
                            />

                            <FloatingLabelInput
                                label={`${t('enter_email') || 'Email'} (${t('optional') || 'міндетті емес'})`}
                                value={email}
                                onChange={handleEmailChange}
                                type="email"
                            />
                            {emailError && (
                                <p className="text-xs text-red-500 -mt-3">{emailError}</p>
                            )}
                            {email && !emailError && (
                                <p className="text-xs text-gray-400 -mt-3">{t('emailHint')}</p>
                            )}

                            {/* Consent */}
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="consent"
                                    checked={isAgreed}
                                    onChange={() => setIsAgreed(!isAgreed)}
                                    className="mt-1 w-4 h-4 text-[#147F26] border-gray-300 rounded focus:ring-[#147F26]"
                                />
                                <label htmlFor="consent" className="text-sm text-gray-600">
                                    {t('consentText') || 'Мен'}{' '}
                                    <a href="/aboutus#policy" target="_blank" rel="noopener noreferrer" className="text-[#147F26] hover:underline">
                                        {t('privacyPolicy') || 'Құпиялылық саясатына'}
                                    </a>
                                    {' '}{t('and') || 'және'}{' '}
                                    <a href="/aboutus#terms" target="_blank" rel="noopener noreferrer" className="text-[#147F26] hover:underline">
                                        {t('termsOfUse') || 'Пайдалану шарттарына'}
                                    </a>
                                    {' '}{t('consentAgree') || 'келісемін'}
                                </label>
                            </div>

                            {errorMessage && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{errorMessage}</p>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={!isAgreed || isLoading}
                                className="w-full py-4 bg-[#147F26] hover:bg-[#0f6820] disabled:bg-gray-300 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#147F26]/25"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        {t('loading') || 'Жүктелуде...'}
                                    </>
                                ) : (
                                    <>
                                        {t('signUp') || 'Тіркелу'}
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </form>

                        <p className="mt-6 text-center text-sm text-gray-500">
                            {t('alreadyHaveAccount') || 'Аккаунтыңыз бар ба?'}{' '}
                            <button
                                onClick={() => navigate('/sign')}
                                className="text-[#147F26] font-semibold hover:underline"
                            >
                                {t('signIn') || 'Кіру'}
                            </button>
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 text-center text-xs text-gray-400 flex-shrink-0 border-t border-gray-100">
                    {t('protectedBy') || 'Қорғалған'}{' '}
                    <a href="/aboutus#terms" className="text-[#147F26] hover:underline">{t('termsOfUse') || 'Пайдалану шарттары'}</a>
                </div>
            </div>
        </div>
    );
};

export default Register;
